import type { PlanDraft } from "@weights/plan-reading";
import { type Change, applyChanges, isNoop } from "./patch.js";
import type { Selection } from "./selection.js";

/** One user intent (grid doc §4): a commit, a paste, a fill, a clear, a column operation. */
export interface Step {
  label: string;
  changes: Change[];
  /** Where the change happened, restored on undo and redo. */
  selection?: Selection;
}

export interface History {
  past: Step[];
  future: Step[];
}

export const HISTORY_LIMIT = 100;

export const emptyHistory: History = { past: [], future: [] };

export function record(history: History, step: Step, limit = HISTORY_LIMIT): History {
  const changes = step.changes.filter((c) => !isNoop(c));
  if (changes.length === 0) return history;
  const past = [...history.past, { ...step, changes }];
  return { past: past.length > limit ? past.slice(past.length - limit) : past, future: [] };
}

export function undo(history: History, draft: PlanDraft): { history: History; draft: PlanDraft; step: Step } | null {
  const step = history.past[history.past.length - 1];
  if (!step) return null;
  return {
    step,
    draft: applyChanges(draft, step.changes, "back"),
    history: { past: history.past.slice(0, -1), future: [...history.future, step] },
  };
}

export function redo(history: History, draft: PlanDraft): { history: History; draft: PlanDraft; step: Step } | null {
  const step = history.future[history.future.length - 1];
  if (!step) return null;
  return {
    step,
    draft: applyChanges(draft, step.changes, "forward"),
    history: { past: [...history.past, step], future: history.future.slice(0, -1) },
  };
}
